/**
 * Click Tracking Service
 *
 * Sends product click events to Meilisearch for analytics.
 * Only works in Bitrix mode where server-side proxy is available.
 */

import { isBitrixMode, buildBitrixApiUrl } from "../utils/bitrixApiUrl";

interface ClickTrackingRequest {
  /** Product ID (internal ID) - server will fetch XML_ID */
  productId: number | string;
  /** Search query that led to the click */
  search: string;
  /** Optional: XML_ID if already available (faster, skips Bitrix query) */
  id?: string;
}

interface ClickTrackingResponse {
  ok?: boolean;
  error?: string;
}

/**
 * Track product click from search results
 *
 * Uses navigator.sendBeacon for guaranteed delivery even during page navigation.
 * Falls back to fetch if sendBeacon is not available.
 * Sends productId to server, which fetches XML_ID from Bitrix.
 *
 * @param productId - Product ID (internal ID)
 * @param searchQuery - Search query that led to the click
 * @returns Promise that resolves when tracking is complete
 */
export async function trackProductClick(
  productId: number | string,
  searchQuery: string
): Promise<boolean> {
  // Only track in Bitrix mode
  if (!isBitrixMode()) {
    return false;
  }

  // Validate inputs
  if (!productId || !searchQuery) {
    return false;
  }

  try {
    const endpoint = buildBitrixApiUrl("/api/click-tracking");

    const requestBody: ClickTrackingRequest = {
      productId,
      search: searchQuery,
    };

    // Use sendBeacon for guaranteed delivery during page navigation
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(requestBody)], {
        type: "application/json",
      });

      const success = navigator.sendBeacon(endpoint, blob);
      return success;
    }

    // Fallback to regular fetch if sendBeacon is not available
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
      signal: AbortSignal.timeout(2000),
    });

    if (!response.ok) {
      return false;
    }

    const data: ClickTrackingResponse = await response.json();
    return data.ok !== false;
  } catch (error) {
    // Silent fail - don't break user experience
    return false;
  }
}

/**
 * Extract product ID from product data
 * Works with both ProductResult and ProductHit types
 */
export function extractProductIdFromProduct(product: unknown): number | string | undefined {
  if (!product || typeof product !== "object") {
    return undefined;
  }

  const productObj = product as Record<string, unknown>;

  // Try product.id first (most common)
  if (
    productObj.id !== undefined &&
    (typeof productObj.id === "string" || typeof productObj.id === "number")
  ) {
    return productObj.id;
  }

  // Try raw hit data
  if (productObj.raw && typeof productObj.raw === "object") {
    const raw = productObj.raw as Record<string, unknown>;

    // Try raw.id first
    if (raw.id !== undefined && (typeof raw.id === "string" || typeof raw.id === "number")) {
      return raw.id;
    }

    // Try raw.productId or raw.offerId (Bitrix fields)
    const idCandidates = [raw.productId, raw.offerId];
    for (const candidate of idCandidates) {
      if (
        candidate !== undefined &&
        (typeof candidate === "string" || typeof candidate === "number")
      ) {
        return candidate;
      }
    }
  }

  return undefined;
}
